<script>
import { mapState } from "vuex";
import { api } from "@/state/services";

export default {
  data() {
    return {
      showModal: false,
      loading: false,
      submitting: false,
      html: "",
      version: "",
      changes: [],
      scrolledToEnd: false,
      error: "",
    };
  },
  computed: {
    // Já tinha aceitado uma versão anterior: o popup vira "Atualização do
    // Regulamento" e mostra o resumo do que mudou (escrito no backoffice).
    isUpdate() {
      return this.changes.length > 0;
    },
    ...mapState({
      account: (state) => state.account,
    }),
    pendingPath() {
      return this.account && this.account.afiliadosRegulamentoPendingPath;
    },
  },
  watch: {
    pendingPath: {
      immediate: true,
      handler(val) {
        if (val) {
          this.openModal();
        } else {
          this.showModal = false;
        }
      },
    },
  },
  methods: {
    openModal() {
      this.showModal = true;
      this.scrolledToEnd = false;
      this.error = "";
      this.changes = [];
      this.loading = true;
      api
        .get("user/regulamento")
        .then((response) => {
          if (response.data.status === "success") {
            this.html = response.data.html;
            this.version = response.data.version;
            this.changes = response.data.changes || [];
          } else {
            this.error = "Não foi possível carregar o regulamento.";
          }
        })
        .catch(() => {
          this.error = "Não foi possível carregar o regulamento.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatDate(value) {
      // "YYYY-MM-DD HH:MM:SS" (horário do Brasil, gravado pelo backoffice).
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
      return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
    },
    onScroll(e) {
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
        this.scrolledToEnd = true;
      }
    },
    closeAndGoToPendingPath() {
      const path = this.pendingPath;
      this.$store.state.account.afiliadosRegulamentoPendingPath = null;
      this.showModal = false;
      if (path) {
        this.$router.push(path).catch(() => {});
      }
    },
    accept() {
      this.error = "";
      this.submitting = true;
      api
        .post("user/regulamento", { decision: "accepted", version: this.version })
        .then((response) => {
          if (response.data.status === "success") {
            const user = { ...this.account.user, afiliados_regulamento_status: "accepted" };
            this.$store.state.account.user = user;
            localStorage.setItem("user", JSON.stringify(user));
            this.closeAndGoToPendingPath();
          } else if (response.data.code === "version_changed") {
            // O backoffice publicou outra versão enquanto a pessoa lia:
            // recarrega o texto novo e exige rolar até o fim de novo.
            this.openModal();
            this.$nextTick(() => {
              this.error = response.data.message;
            });
          } else {
            this.error = response.data.message || "Não foi possível registrar o aceite.";
          }
        })
        .catch(() => {
          this.error = "Não foi possível registrar o aceite. Tente novamente.";
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    decline() {
      this.$bvModal
        .msgBoxConfirm(
          "Deseja realmente rejeitar o regulamento? Você perderá o acesso à seção de Afiliados até aceitar o Regulamento novamente.",
          {
            title: "Confirmar recusa",
            okVariant: "danger",
            okTitle: "Recusar",
            cancelTitle: "Cancelar",
            centered: true,
            noCloseOnBackdrop: true,
          }
        )
        .then((confirmed) => {
          if (!confirmed) return;

          this.submitting = true;
          api
            .post("user/regulamento", { decision: "declined" })
            .then((response) => {
              if (response.data.status === "success") {
                const user = { ...this.account.user, afiliados_regulamento_status: "declined" };
                this.$store.state.account.user = user;
                localStorage.setItem("user", JSON.stringify(user));
              }
            })
            .finally(() => {
              this.submitting = false;
              this.$store.state.account.afiliadosRegulamentoPendingPath = null;
              this.showModal = false;
              this.$router.push("/").catch(() => {});
            });
        });
    },
  },
};
</script>

<template>
  <b-modal
    v-model="showModal"
    :title="isUpdate ? 'Atualização do Regulamento do Programa de Afiliados' : 'Regulamento do Programa de Afiliados'"
    hide-header-close
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    centered
    size="lg"
  >
    <div v-if="loading" class="text-center py-4">
      <b-spinner></b-spinner>
    </div>
    <template v-else>
      <div v-if="isUpdate" class="alert alert-warning small mb-3">
        <strong>Nossos termos foram atualizados.</strong>
        Para continuar, leia a nova versão e aceite novamente.
        <div v-for="c in changes" :key="c.version" class="mt-2">
          <div class="font-weight-bold">
            Versão {{ c.version }} — {{ formatDate(c.date) }}
          </div>
          <div v-if="c.resumo" style="white-space: pre-line;">{{ c.resumo }}</div>
        </div>
      </div>
      <div
        class="regulamento-scroll"
        style="max-height: 50vh; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 4px; padding: 16px;"
        @scroll="onScroll"
        v-html="html"
      ></div>
      <p class="small text-muted mt-2 mb-0">
        Role o texto até o final para habilitar o botão de aceite.
      </p>
      <div v-if="error" class="alert alert-danger py-2 small mt-2">{{ error }}</div>
      <div class="d-flex justify-content-end mt-3">
        <b-button variant="outline-danger" class="mr-2" :disabled="submitting" @click="decline">
          Recusar
        </b-button>
        <b-button variant="primary" :disabled="!scrolledToEnd || submitting" @click="accept">
          <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
          {{ isUpdate ? "Aceitar nova versão" : "Aceitar Regulamento" }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>
